import { useCallback, useMemo } from "react";
import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";
import type * as Shared from "@discord-bldr/shared";

export type {
  TicketFormField,
  TicketMessageAttachment,
  TicketMessageComponentItem,
  TicketMessageContainer,
  TicketMessageEmbed,
  TicketPanelEmbed,
  TicketPanelEmbedField,
  TicketMessageSource,
  TicketParticipant,
  TicketTag,
  TicketSnapshot as TicketTranscriptSnapshot,
  MessageSnapshot as TranscriptMessage,
  ParticipantSnapshot as TranscriptParticipant,
} from "@discord-bldr/shared";

export type Ticket = Shared.TicketResponse;
export type TicketCategory = Shared.TicketCategoryResponse;
export type TicketMessage = Shared.TicketMessageResponse;
export type TicketSettings = Shared.TicketSettingsResponse;
export type TicketTranscript = Shared.TicketTranscriptData;

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  awaitingResponse: number;
  resolved: number;
  closed: number;
  averageResponseTime: number | null;
  averageResolutionTime: number | null;
  ticketsToday: number;
  ticketsThisWeek: number;
  ticketsThisMonth: number;
}

export interface TicketCategoryDocument {
  id: number;
  applicationGuildId: string;
  sourceType: string;
  sourceUrl: string | null;
  title: string | null;
  tags: string[] | null;
  createdAt: string;
}

export interface TicketCategoryDocumentsResponse {
  data: { documents: TicketCategoryDocument[] };
}

export const ticketsKeys = {
  all: ["tickets"] as const,
  list: (applicationGuildId: string) =>
    [...ticketsKeys.all, "list", applicationGuildId] as const,
  detail: (applicationGuildId: string, ticketId: number) =>
    [...ticketsKeys.all, "detail", applicationGuildId, ticketId] as const,
  messages: (applicationGuildId: string, ticketId: number) =>
    [...ticketsKeys.all, "messages", applicationGuildId, ticketId] as const,
  transcript: (applicationGuildId: string, ticketId: number) =>
    [...ticketsKeys.all, "transcript", applicationGuildId, ticketId] as const,
  settings: (applicationGuildId: string) =>
    [...ticketsKeys.all, "settings", applicationGuildId] as const,
  categories: (applicationGuildId: string) =>
    [...ticketsKeys.all, "categories", applicationGuildId] as const,
  categoryDocuments: (applicationGuildId: string, categoryId: number) =>
    [
      ...ticketsKeys.all,
      "category-documents",
      applicationGuildId,
      categoryId,
    ] as const,
  stats: (applicationGuildId: string, categoryIds?: number[]) =>
    [...ticketsKeys.all, "stats", applicationGuildId, categoryIds] as const,
};

export interface TicketsListResponse {
  data: {
    tickets: Ticket[];
    total: number;
    limit: number;
    offset: number;
  };
}

export interface TicketFilters {
  status?: Ticket["status"];
  subStatus?: Ticket["subStatus"];
  priority?: Ticket["priority"];
  categoryId?: number;
  assigneeDiscordId?: string;
  creatorDiscordId?: string;
  search?: string;
  ticketType?: "channel" | "modmail";
  tag?: string;
  excludeClosed?: boolean;
  limit?: number;
  offset?: number;
}

export const ticketsQueryOptions = (
  applicationGuildId: string,
  filters: TicketFilters
) => {
  const { limit = 20, offset = 0 } = filters;
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });

  if (filters.status) params.append("status", filters.status);
  if (filters.subStatus) params.append("subStatus", filters.subStatus);
  if (filters.priority) params.append("priority", filters.priority);
  if (filters.categoryId)
    params.append("categoryId", String(filters.categoryId));
  if (filters.assigneeDiscordId)
    params.append("assigneeDiscordId", filters.assigneeDiscordId);
  if (filters.creatorDiscordId)
    params.append("creatorDiscordId", filters.creatorDiscordId);
  if (filters.search) params.append("search", filters.search);
  if (filters.ticketType) params.append("ticketType", filters.ticketType);
  if (filters.tag) params.append("tag", filters.tag);
  if (filters.excludeClosed) params.append("excludeClosed", "true");

  return queryOptions({
    queryKey: [...ticketsKeys.list(applicationGuildId), filters],
    queryFn: () =>
      apiClient
        .get<TicketsListResponse>(
          `/api/guilds/${applicationGuildId}/tickets?${params.toString()}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      if (document.hidden) return false;
      const tickets = query.state.data?.data?.tickets || [];
      const hasOpenTickets = tickets.some(
        (t) => t.status !== "closed" && t.status !== "resolved"
      );
      return hasOpenTickets ? 30000 : false;
    },
  });
};

export function useTickets(
  applicationGuildId: string,
  filters: TicketFilters = {}
) {
  return useQuery(ticketsQueryOptions(applicationGuildId, filters));
}

export interface TicketDetailResponse {
  data: Ticket & {
    participants: Shared.TicketParticipant[];
    transcriptPublicId: string | null;
  };
}

export const ticketQueryOptions = (
  applicationGuildId: string,
  ticketId: number,
  options: { polling?: boolean }
) => {
  const { polling = true } = options;

  return queryOptions({
    queryKey: ticketsKeys.detail(applicationGuildId, ticketId),
    queryFn: () =>
      apiClient
        .get<TicketDetailResponse>(
          `/api/guilds/${applicationGuildId}/tickets/${ticketId}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && !!ticketId,
    refetchInterval: polling
      ? (query) => {
          if (document.hidden) return false;
          const ticket = query.state.data?.data;
          if (ticket?.status === "closed") return false;
          return 5000;
        }
      : false,
  });
};

export function useTicket(
  applicationGuildId: string,
  ticketId: number,
  options: { polling?: boolean } = {}
) {
  return useQuery(ticketQueryOptions(applicationGuildId, ticketId, options));
}

interface TicketMessagesResponse {
  data: {
    messages: TicketMessage[];
    ticketId: number;
    hasMore: boolean;
  };
}

type MessagesPage = TicketMessagesResponse["data"];

async function fetchTicketMessages(
  applicationGuildId: string,
  ticketId: number,
  params: {
    limit?: number;
    before?: string;
    beforeId?: number;
    sort?: "asc" | "desc";
  }
): Promise<MessagesPage> {
  const qs = new URLSearchParams();
  if (params.limit !== undefined) qs.append("limit", String(params.limit));
  if (params.before) qs.append("before", params.before);
  if (params.beforeId !== undefined)
    qs.append("beforeId", String(params.beforeId));
  if (params.sort) qs.append("sort", params.sort);

  return apiClient
    .get<TicketMessagesResponse>(
      `/api/guilds/${applicationGuildId}/tickets/${ticketId}/messages?${qs.toString()}`
    )
    .then((r) => r.data.data);
}

export interface TicketTranscriptResponse {
  data: TicketTranscript;
}

const publicTranscriptQueryOptions = (transcriptId: string) =>
  queryOptions({
    queryKey: ["publicTranscript", transcriptId],
    queryFn: () =>
      apiClient
        .get<TicketTranscriptResponse>(`/api/transcripts/${transcriptId}`)
        .then((r) => r.data),
    enabled: !!transcriptId,
  });

export function usePublicTranscript(transcriptId: string) {
  return useQuery(publicTranscriptQueryOptions(transcriptId));
}

export interface TicketSettingsResponse {
  data: TicketSettings;
}

export const ticketSettingsQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: ticketsKeys.settings(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<TicketSettingsResponse>(
          `/api/guilds/${applicationGuildId}/tickets/settings`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    staleTime: 60_000,
  });

export function useTicketSettings(applicationGuildId: string) {
  return useQuery(ticketSettingsQueryOptions(applicationGuildId));
}

export function useUpdateTicketSettings(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<TicketSettings>) =>
      apiClient
        .patch<TicketSettingsResponse>(
          `/api/guilds/${applicationGuildId}/tickets/settings`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.settings(applicationGuildId),
      });
    },
  });
}

export interface DeployPanelInput {
  channelId: string;
}

export interface DeployPanelResponse {
  data: {
    messageId: string;
    channelId: string;
  };
}

export function useDeployPanel(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeployPanelInput) =>
      apiClient
        .post<DeployPanelResponse>(
          `/api/guilds/${applicationGuildId}/tickets/panel/deploy`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.settings(applicationGuildId),
      });
    },
  });
}

export interface TicketCategoriesResponse {
  data: TicketCategory[];
}

export const ticketCategoriesQueryOptions = (applicationGuildId: string) =>
  queryOptions({
    queryKey: ticketsKeys.categories(applicationGuildId),
    queryFn: () =>
      apiClient
        .get<TicketCategoriesResponse>(
          `/api/guilds/${applicationGuildId}/tickets/categories`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
  });

export function useTicketCategories(applicationGuildId: string) {
  return useQuery(ticketCategoriesQueryOptions(applicationGuildId));
}

export interface CreateCategoryInput {
  name: string;
  description?: string | null;
  emoji?: string | null;
  discordCategoryId?: string | null;
  supportRoleIds?: string[] | null;
  welcomeMessage?: string | null;
  modalEnabled?: boolean;
  modalTitle?: string | null;
  formFields?: Shared.TicketFormField[] | null;
  position?: number;
  isEnabled?: boolean;
  aiAutoResponseEnabled?: boolean;
  aiUseKnowledgeBase?: boolean;
  aiSummaryOnClose?: boolean;
  aiUsePastResolutions?: boolean;
  oneTicketPerUser?: boolean;
  autoDeleteOnClose?: boolean;
  closedTicketsCategoryId?: string | null;
}

export interface CategoryResponse {
  data: TicketCategory;
}

export function useCreateCategory(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCategoryInput) =>
      apiClient
        .post<CategoryResponse>(
          `/api/guilds/${applicationGuildId}/tickets/categories`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categories(applicationGuildId),
      });
    },
  });
}

export function useUpdateCategory(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      ...data
    }: Partial<CreateCategoryInput> & { categoryId: number }) =>
      apiClient
        .patch<CategoryResponse>(
          `/api/guilds/${applicationGuildId}/tickets/categories/${categoryId}`,
          data
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categories(applicationGuildId),
      });
    },
  });
}

export function useDeleteCategory(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: number) =>
      apiClient
        .delete<{
          data: { success: boolean; id: number };
        }>(`/api/guilds/${applicationGuildId}/tickets/categories/${categoryId}`)
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categories(applicationGuildId),
      });
    },
  });
}

export function useReorderCategories(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryIds: number[]) =>
      apiClient
        .post<TicketCategoriesResponse>(
          `/api/guilds/${applicationGuildId}/tickets/categories/reorder`,
          { categoryIds }
        )
        .then((r) => r.data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categories(applicationGuildId),
      });
    },
  });
}

export function useCategoryDocuments(
  applicationGuildId: string,
  categoryId: number | undefined
) {
  return useQuery({
    queryKey: ticketsKeys.categoryDocuments(
      applicationGuildId,
      categoryId ?? -1
    ),
    queryFn: () =>
      apiClient
        .get<TicketCategoryDocumentsResponse>(
          `/api/guilds/${applicationGuildId}/tickets/categories/${categoryId}/documents`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && categoryId !== undefined,
  });
}

export function useUpdateCategoryDocuments(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      documentIds,
    }: {
      categoryId: number;
      documentIds: number[];
    }) =>
      apiClient
        .put(
          `/api/guilds/${applicationGuildId}/tickets/categories/${categoryId}/documents`,
          { documentIds }
        )
        .then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categoryDocuments(
          applicationGuildId,
          variables.categoryId
        ),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.categories(applicationGuildId),
      });
    },
  });
}

export interface TicketStatsResponse {
  data: TicketStats;
}

export const ticketStatsQueryOptions = (
  applicationGuildId: string,
  categoryIds?: number[]
) => {
  const params = new URLSearchParams();
  if (categoryIds && categoryIds.length > 0) {
    params.append("categoryIds", categoryIds.join(","));
  }
  const qs = params.toString();

  return queryOptions({
    queryKey: ticketsKeys.stats(applicationGuildId, categoryIds),
    queryFn: () =>
      apiClient
        .get<TicketStatsResponse>(
          `/api/guilds/${applicationGuildId}/tickets/stats${qs ? `?${qs}` : ""}`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId,
    staleTime: 5000,
    placeholderData: keepPreviousData,
  });
};

export function useTicketStats(
  applicationGuildId: string,
  categoryIds?: number[]
) {
  return useQuery(ticketStatsQueryOptions(applicationGuildId, categoryIds));
}

export interface EligibleStaffMember {
  discordId: string;
  username: string;
  avatar: string | null;
}

export function useEligibleStaff(applicationGuildId: string, ticketId: number) {
  return useQuery({
    queryKey: [
      ...ticketsKeys.detail(applicationGuildId, ticketId),
      "eligible-staff",
    ],
    queryFn: () =>
      apiClient
        .get<{
          data: EligibleStaffMember[];
        }>(
          `/api/guilds/${applicationGuildId}/tickets/${ticketId}/eligible-staff`
        )
        .then((r) => r.data),
    enabled: !!applicationGuildId && !!ticketId,
    staleTime: 60_000,
  });
}

export interface UpdateTicketInput {
  ticketId: number;
  status?: Ticket["status"];
  priority?: Ticket["priority"];
  categoryId?: number | null;
  assigneeDiscordId?: string | null;
  assigneeUsername?: string | null;
  tags?: string[];
  idleExempt?: boolean;
}

export function useLiveUpdateTicket(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, ...data }: UpdateTicketInput) =>
      apiClient
        .patch<{
          data: Ticket;
        }>(`/api/guilds/${applicationGuildId}/tickets/${ticketId}/live`, data)
        .then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.list(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.detail(applicationGuildId, variables.ticketId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.stats(applicationGuildId),
      });
    },
  });
}

export function useCloseTicket(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ticketId, reason }: { ticketId: number; reason?: string }) =>
      apiClient
        .post<{
          data: Ticket;
        }>(`/api/guilds/${applicationGuildId}/tickets/${ticketId}/close`, {
          reason,
        })
        .then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.list(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.detail(applicationGuildId, variables.ticketId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.stats(applicationGuildId),
      });
    },
  });
}

export function useClaimTicket(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ticketId: number) =>
      apiClient
        .post<{
          data: Ticket;
        }>(`/api/guilds/${applicationGuildId}/tickets/${ticketId}/claim`)
        .then((r) => r.data),
    onSuccess: (_, ticketId) => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.list(applicationGuildId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.detail(applicationGuildId, ticketId),
      });
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.stats(applicationGuildId),
      });
    },
  });
}

// ============ Resolution Check ============

export function useSendResolutionCheck(applicationGuildId: string) {
  return useMutation({
    mutationFn: (ticketId: number) =>
      apiClient
        .post<{
          data: { sent: boolean };
        }>(
          `/api/guilds/${applicationGuildId}/tickets/${ticketId}/resolution-check`
        )
        .then((r) => r.data),
  });
}

// SSE events are imported from @discord-bldr/shared
export type { SSETicketEvent } from "@discord-bldr/shared";
type SSETicketEvent = Shared.SSETicketEvent;

// ============ Live Chat: Send Message ============

export interface SendMessageInput {
  ticketId: number;
  content: string;
  clientMessageId?: string;
  mentions?: Record<string, string>;
}

export function useSendTicketMessage(applicationGuildId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      ticketId,
      content,
      clientMessageId,
      mentions,
    }: SendMessageInput) =>
      apiClient
        .post<{
          data: TicketMessage;
        }>(`/api/guilds/${applicationGuildId}/tickets/${ticketId}/messages`, {
          content,
          clientMessageId,
          mentions,
        })
        .then((r) => r.data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ticketsKeys.detail(applicationGuildId, variables.ticketId),
      });
    },
  });
}

// ============ Live Chat: Edit Message ============

export interface EditMessageInput {
  ticketId: number;
  messageId: number;
  content: string;
  mentions?: Record<string, string>;
}

export function useEditTicketMessage(applicationGuildId: string) {
  return useMutation({
    mutationFn: ({
      ticketId,
      messageId,
      content,
      mentions,
    }: EditMessageInput) =>
      apiClient
        .put<{
          data: TicketMessage;
        }>(
          `/api/guilds/${applicationGuildId}/tickets/${ticketId}/messages/${messageId}`,
          { content, mentions }
        )
        .then((r) => r.data),
  });
}

interface DeleteMessageInput {
  ticketId: number;
  messageId: number;
}

export function useDeleteTicketMessage(applicationGuildId: string) {
  return useMutation({
    mutationFn: ({ ticketId, messageId }: DeleteMessageInput) =>
      apiClient
        .delete<{
          data: TicketMessage;
        }>(
          `/api/guilds/${applicationGuildId}/tickets/${ticketId}/messages/${messageId}`
        )
        .then((r) => r.data),
  });
}

// ============ Live Chat: Infinite Query + Cache-only SSE ============

type MessagesInfiniteData = InfiniteData<MessagesPage, string | undefined>;

function mapAllMessages(
  old: MessagesInfiniteData,
  fn: (msg: TicketMessage) => TicketMessage
): MessagesInfiniteData {
  return {
    ...old,
    pages: old.pages.map((page) => ({
      ...page,
      messages: page.messages.map(fn),
    })),
  };
}

function appendToLastPage(
  old: MessagesInfiniteData,
  msg: TicketMessage
): MessagesInfiniteData {
  const pages = [...old.pages];
  const lastIdx = pages.length - 1;
  pages[lastIdx] = {
    ...pages[lastIdx],
    messages: [...pages[lastIdx].messages, msg],
  };
  return { ...old, pages };
}

export function useTicketLiveMessages(
  applicationGuildId: string,
  ticketId: number
) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ticketsKeys.messages(applicationGuildId, ticketId),
    [applicationGuildId, ticketId]
  );

  const {
    data,
    isPending,
    hasPreviousPage,
    isFetchingPreviousPage,
    fetchPreviousPage,
  } = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchTicketMessages(applicationGuildId, ticketId, {
        sort: "desc",
        beforeId: pageParam,
      }),
    initialPageParam: undefined as number | undefined,
    getPreviousPageParam: (firstPage) =>
      firstPage.hasMore ? firstPage.messages[0]?.id : undefined,
    getNextPageParam: () => undefined,
    enabled: !!applicationGuildId && !!ticketId,
    gcTime: 0,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // Pages are [oldest..newest] after fetchPreviousPage prepends; flatMap gives chronological order
  const messages = useMemo(
    () => data?.pages.flatMap((p) => p.messages) ?? [],
    [data]
  );

  const updateCache = useCallback(
    (updater: (old: MessagesInfiniteData) => MessagesInfiniteData) => {
      queryClient.setQueryData<MessagesInfiniteData>(queryKey, (old) =>
        old ? updater(old) : old
      );
    },
    [queryClient, queryKey]
  );

  const addOptimisticMessage = useCallback(
    (msg: {
      content: string;
      clientMessageId: string;
      authorUsername: string;
      authorAvatar: string | null;
      authorDiscordId: string;
      mentions?: Record<string, string>;
    }) => {
      const optimistic: TicketMessage = {
        id: -Date.now(),
        ticketId,
        discordMessageId: null,
        authorDiscordId: msg.authorDiscordId,
        authorUsername: msg.authorUsername,
        authorAvatar: msg.authorAvatar,
        authorIsBot: false,
        authorIsStaff: true,
        content: msg.content,
        attachments: null,
        embeds: null,
        components: null,
        sources: null,
        mentions: msg.mentions ?? null,
        channels: null,
        roles: null,
        replyToDiscordMessageId: null,
        replyToAuthorDiscordId: null,
        replyToAuthorUsername: null,
        replyToAuthorAvatar: null,
        replyToContent: null,
        source: "dashboard",
        pending: true,
        clientMessageId: msg.clientMessageId,
        isEdited: false,
        editedAt: null,
        isDeleted: false,
        deletedAt: null,
        createdAt: new Date().toISOString(),
      };
      updateCache((old) => appendToLastPage(old, optimistic));
    },
    [ticketId, updateCache]
  );

  const handleSSEEvent = useCallback(
    (event: SSETicketEvent) => {
      switch (event.type) {
        case "message:created": {
          updateCache((old) => {
            // Check if this message already exists (optimistic or duplicate)
            let exists = false;
            for (const page of old.pages) {
              for (const m of page.messages) {
                if (
                  m.id === event.message.id ||
                  (event.message.clientMessageId &&
                    m.clientMessageId === event.message.clientMessageId)
                ) {
                  exists = true;
                  break;
                }
              }
              if (exists) break;
            }

            if (exists) {
              return mapAllMessages(old, (m) =>
                (m.clientMessageId &&
                  m.clientMessageId === event.message.clientMessageId) ||
                m.id === event.message.id
                  ? { ...event.message }
                  : m
              );
            }
            return appendToLastPage(old, event.message);
          });
          break;
        }
        case "message:confirmed": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              m.id === event.messageId ||
              (event.clientMessageId &&
                m.clientMessageId === event.clientMessageId)
                ? {
                    ...m,
                    discordMessageId: event.discordMessageId,
                    pending: false,
                  }
                : m
            )
          );
          break;
        }
        case "message:failed": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              m.id === event.messageId ||
              (event.clientMessageId &&
                m.clientMessageId === event.clientMessageId)
                ? ({
                    ...m,
                    pending: false,
                    _failed: true,
                  } as TicketMessage & { _failed?: boolean })
                : m
            )
          );
          break;
        }
        case "message:edited": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              (event.discordMessageId &&
                m.discordMessageId === event.discordMessageId) ||
              (event.messageId && m.id === event.messageId)
                ? {
                    ...m,
                    content: event.content,
                    mentions:
                      "mentions" in event
                        ? (event.mentions ?? null)
                        : m.mentions,
                    isEdited: true,
                    _editPending: event.pending ?? false,
                  }
                : m
            )
          );
          break;
        }
        case "message:edit_confirmed": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              m.id === event.messageId ? { ...m, _editPending: false } : m
            )
          );
          break;
        }
        case "message:edit_failed": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              m.id === event.messageId
                ? {
                    ...m,
                    content: event.originalContent,
                    mentions: event.originalMentions ?? m.mentions,
                    _editPending: false,
                    isEdited: false,
                  }
                : m
            )
          );
          break;
        }
        case "message:deleted": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              (event.discordMessageId &&
                m.discordMessageId === event.discordMessageId) ||
              (event.messageId && m.id === event.messageId)
                ? { ...m, isDeleted: true }
                : m
            )
          );
          break;
        }
        case "message:delete_confirmed": {
          // Already marked as deleted — nothing to do
          break;
        }
        case "message:delete_failed": {
          updateCache((old) =>
            mapAllMessages(old, (m) =>
              m.id === event.messageId ? { ...m, isDeleted: false } : m
            )
          );
          break;
        }
        case "ticket:updated": {
          queryClient.invalidateQueries({
            queryKey: ticketsKeys.detail(applicationGuildId, ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: ticketsKeys.list(applicationGuildId),
          });
          break;
        }
        case "ticket:action_failed": {
          queryClient.invalidateQueries({
            queryKey: ticketsKeys.detail(applicationGuildId, ticketId),
          });
          queryClient.invalidateQueries({
            queryKey: ticketsKeys.list(applicationGuildId),
          });
          break;
        }
      }
    },
    [applicationGuildId, ticketId, queryClient, updateCache]
  );

  return {
    messages,
    isLoading: isPending,
    handleSSEEvent,
    addOptimisticMessage,
    hasMore: hasPreviousPage,
    isFetchingMore: isFetchingPreviousPage,
    fetchOlderMessages: fetchPreviousPage,
  };
}
